import { useRef, useState } from "react";
import CasesMenu from "../../components/cases-page/cases-menu/cases-menu";
import PageTitle from "../../components/common/page-title/page-title";
import { getCasesApi, getCasesByTagsApi, getCaseTagsApi } from "../../api/api";
import { useEffect } from "react";
import { TCase, TCaseTag } from "../../types/types";
import Loading from "../../components/common/loading/loading";
import Case from "../../components/common/cases-mock/case";
import iCasesStarDesktop from "../../assets/icons/cases-block/star-desktop.svg";
import "./cases-page.sass";

const CasesPage = () => {
  const [selected, setSelected] = useState<
    number | { index: number; slug: string }[]
  >(0);
  const [cases, setCases] = useState<TCase[]>([]);
  const [isCasesLoading, setCasesLoading] = useState(true);
  const [isCaseTagsLoading, setCaseTagsLoading] = useState(true);
  const [caseTags, setCaseTags] = useState<TCaseTag[]>([]);
  const [casesError, setCasesError] = useState("");
  const [tagsError, setTagsError] = useState("");
  const [isCasesRefetching, setCasesRefetching] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const isInitialSelectedSyncRef = useRef(true);

  const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === "AbortError";

  const loadCasesBySelected = (
    nextSelected: number | { index: number; slug: string }[],
    signal?: AbortSignal,
  ) => {
    if (nextSelected === 0) {
      return getCasesApi({ signal });
    }

    if (Array.isArray(nextSelected)) {
      return getCasesByTagsApi(
        nextSelected.map((item) => item.slug),
        { signal },
      );
    }

    return Promise.resolve({ data: [] as TCase[] });
  };

  useEffect(() => {
    const tagsController = new AbortController();
    const casesController = new AbortController();

    getCaseTagsApi({ signal: tagsController.signal })
      .then((data) => {
        setCaseTags(data.data);
        setTagsError("");
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setTagsError("Не удалось загрузить теги кейсов.");
      })
      .finally(() => {
        if (!tagsController.signal.aborted) {
          setCaseTagsLoading(false);
        }
      });

    getCasesApi({ signal: casesController.signal })
      .then((data) => {
        setCases(data.data);
        setCasesError("");
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setCasesError("Не удалось загрузить кейсы.");
      })
      .finally(() => {
        if (!casesController.signal.aborted) {
          setCasesLoading(false);
        }
      });

    return () => {
      tagsController.abort();
      casesController.abort();
    };
  }, [reloadKey]);

  useEffect(() => {
    if (isInitialSelectedSyncRef.current) {
      isInitialSelectedSyncRef.current = false;
      return;
    }

    const controller = new AbortController();
    setCasesRefetching(true);

    loadCasesBySelected(selected, controller.signal)
        .then((data) => {
          setCases(data.data);
          setCasesError("");
        })
        .catch((error) => {
          if (isAbortError(error)) {
            return;
          }
          setCasesError(
            selected === 0
              ? "Не удалось загрузить кейсы."
              : "Не удалось отфильтровать кейсы.",
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setCasesRefetching(false);
          }
        });

    return () => {
      controller.abort();
    };
  }, [selected]);

  return (
    <div className="page cases-page">
      <img
        src={iCasesStarDesktop}
        className="img-absolute cases-page__star"
        alt=""
      />
      <PageTitle>кейсы</PageTitle>

      <div className="cases-page__content">
        {tagsError && <div className="error">{tagsError}</div>}
        {casesError && <div className="error">{casesError}</div>}

        {!isCaseTagsLoading && (
          <CasesMenu
            menuItems={caseTags}
            selected={selected}
            setSelected={setSelected}
          />
        )}

        <Loading isLoading={isCaseTagsLoading} />

        {!isCasesLoading &&
          !isCasesRefetching &&
          cases.map((caseData) => (
            <Case key={caseData.id} caseData={caseData} />
          ))}

        {!isCasesLoading && !isCasesRefetching && !casesError && cases.length === 0 && (
          <div className="error">Кейсы не найдены.</div>
        )}

        {(casesError || tagsError) && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setCaseTagsLoading(true);
              setCasesLoading(true);
              setReloadKey((prev) => prev + 1);
            }}
          >
            Повторить загрузку
          </button>
        )}

        <Loading isLoading={isCasesLoading || isCasesRefetching} />
      </div>
    </div>
  );
};

export default CasesPage;
